import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { merge, Observable, Subject, Subscription } from "rxjs";
import { startWith, takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import { MatSidenav } from "@angular/material/sidenav";
import { MatExpansionPanel } from "@angular/material/expansion";
import { AuthService } from "../../auth/auth.service";
import { LANG } from "../../app.component";
import { User as FirebaseUser } from "@angular/fire/auth";
import { Store } from "@ngrx/store";
import { getCurrentAccount } from "../../accounts/accounts.reducer";
import { User } from "../../users/users.interface";
import { Router, NavigationEnd } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { getBrandLogoPath } from "../branding/branding.util";
import { ThemeService } from "../theme/theme.service";

@Component({
  selector: "app-main",
  templateUrl: "./main.component.html",
  styleUrls: ["./main.component.scss"],
  standalone: false,
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSidenav)
  sidenav!: MatSidenav;
  @ViewChildren(MatExpansionPanel)
  expansionPanels: QueryList<MatExpansionPanel>;
  @ViewChild("signaturesPanel") signaturesPanel: MatExpansionPanel;
  @ViewChild("settingsPanel") settingsPanel: MatExpansionPanel;
  @ViewChild("billingPanel") billingPanel: MatExpansionPanel;
  @ViewChild("myProfilePanel") myProfilePanel: MatExpansionPanel;
  @ViewChild("adminPanel") adminPanel: MatExpansionPanel;
  @ViewChild("backgroundsPanel") backgroundsPanel: MatExpansionPanel;
  allPanelsExpanded = true;
  user: Observable<FirebaseUser>;
  account$: Observable<string>;
  languages = ["en", "es", "pl"];
  selectedLanguage;
  generatorUrl: string;
  isMobile = false;
  private destroy$ = new Subject<void>();
  private panelChangesSub: Subscription;
  signaturesPanelExpanded = false;
  digitalCardsPanelExpanded = false;
  backgroundsPanelExpanded = false;
  settingsPanelExpanded = false;
  billingPanelExpanded = false;
  myProfilePanelExpanded = false;
  adminPanelExpanded = false;
  startProgress = 0;
  isLoadingProgress = false;
  private previousUrl = "";
  readonly logoLight = getBrandLogoPath("black");
  readonly logoDark = getBrandLogoPath("white");

  constructor(
    public auth: AuthService,
    public translate: TranslateService,
    private store: Store,
    public router: Router,
    private observer: BreakpointObserver,
    private http: HttpClient,
    public theme: ThemeService,
  ) {
    translate.addLangs(this.languages);
    translate.setDefaultLang("en");
    const browserLang = translate.getBrowserLang();
    const defaultLang =
      localStorage.getItem(LANG) ||
      (browserLang.match(/en|es|pl/) ? browserLang : "en");
    translate.use(defaultLang);
    this.selectedLanguage = defaultLang;
  }

  ngOnInit(): void {
    this.user = this.auth.authState$;
    this.account$ = this.store.select(getCurrentAccount);
    this.setInitialPanelState();
    void this.loadStartProgress();

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.url;
        const wasOnStart = this.previousUrl === "/start";
        const isOnStart = currentUrl === "/start";

        if ((wasOnStart && !isOnStart) || (!wasOnStart && isOnStart)) {
          void this.loadStartProgress();
        }

        this.previousUrl = currentUrl;
      }
    });
  }

  toggleAllPanels() {
    const shouldCollapseAll = this.expansionPanels.some((p) => p.expanded);

    if (shouldCollapseAll) {
      this.expansionPanels.forEach((p) => p.close());
    } else {
      this.expansionPanels.forEach((p) => p.open());
    }
  }

  redirectToGenerator() {
    const appUser = this.auth.appUser;
    if (appUser) {
      this.auth.getGeneratorToken(appUser).subscribe(
        (token) => {
          const url = `https://generator.gsignature.com/?token=${token}`;
          window.open(url, "_blank");
        },
        (error) => {
          console.error("Error generating token:", error);
        },
      );
    } else {
      console.error("appUser not available");
    }
  }

  goToStart(): void {
    void this.router.navigate(["/start"]);
    this.closeSidenavOnMobile();
  }

  get hideMenu(): boolean {
    return window.location.pathname === "/accounts";
  }

  get appUser(): User | undefined {
    return this.auth.appUser;
  }

  closeSidenavOnMobile(): void {
    if (this.isMobile && this.sidenav) {
      void this.sidenav.close();
    }
  }

  ngAfterViewInit() {
    this.observer
      .observe(["(max-width: 1023px)"])
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (this.hideMenu) {
          return;
        }
        this.isMobile = res.matches;
        if (res.matches) {
          this.sidenav.mode = "over";
          void this.sidenav.close();
        } else {
          this.sidenav.mode = "side";
          void this.sidenav.open();
        }
      });

    this.expansionPanels.changes
      .pipe(startWith(null), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.panelChangesSub) {
          this.panelChangesSub.unsubscribe();
        }

        if (!this.expansionPanels || this.expansionPanels.length === 0) {
          return;
        }

        const panelEvents = this.expansionPanels.map((p) =>
          merge(p.opened, p.closed),
        );
        this.panelChangesSub = merge(...panelEvents)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.updateButtonState();
          });

        setTimeout(() => this.updateButtonState(), 0);
      });
  }

  updateButtonState() {
    if (!this.expansionPanels || this.expansionPanels.length === 0) {
      this.allPanelsExpanded = false;
      return;
    }
    this.allPanelsExpanded = this.expansionPanels.some((p) => p.expanded);
  }

  initials = (name: string) =>
    name ? `${name.split(" ")[0][0]} ${name.split(" ")[1][0]}` : "";

  change = (event: { value: string }) => {
    localStorage.setItem(LANG, event.value);
    this.translate.use(event.value);
  };

  private setInitialPanelState(): void {
    const panelConfig = [
      {
        name: "digitalCards",
        routes: [
          "/digital-cards",
          "/digital-cards/statistics",
          "/digital-cards/sources",
          "/print-cards",
          "/card-orders",
        ],
        isAvailable: this.showDigitalCardsNav,
      },
      {
        name: "signatures",
        routes: ["/signatures/custom", "/planned", "/stats"],
        isAvailable: this.showSignaturesNav,
      },
      {
        name: "backgrounds",
        routes: ["/backgrounds", "/backgrounds/meets"],
        isAvailable: this.showBackgroundsNav,
      },
      {
        name: "settings",
        routes: [
          "/user-admins",
          "/employees",
          "/addons",
          "/media",
          "/settings/company",
          "/settings/employees",
          "/settings/departments",
          "/settings/advanced",
          "/settings/integration",
          "/settings/extension",
          "/settings/api",
        ],
        isAvailable:
          this.auth.isSuperAdmin ||
          this.auth.isCompanyManager ||
          this.auth.isGroupManager ||
          this.auth.isReseller,
      },
      {
        name: "billing",
        routes: ["/settings/billing", "/subscription"],
        isAvailable:
          (this.auth.isSuperAdmin ||
            this.auth.isCompanyManager ||
            this.auth.isGroupManager ||
            this.auth.isReseller) &&
          !this.auth.appUser?.subdomain,
      },
      {
        name: "myProfile",
        routes: [
          "/my-account",
          "/my-digital-cards",
          "/my-backgrounds",
          "/accounts",
        ],
        isAvailable: true,
      },
      {
        name: "admin",
        routes: [
          "/admin/audit-log",
          "/admin/print-card-orders",
          "/admin/client",
          "/admin/reseller",
        ],
        isAvailable: this.auth.isSuperAdmin,
      },
    ];

    for (const config of panelConfig) {
      if (
        config.isAvailable &&
        config.routes.some((route) => this.router.isActive(route, false))
      ) {
        this[`${config.name}PanelExpanded`] = true;
      }
    }

    const activePanelFound = panelConfig.some(
      (config) =>
        config.isAvailable &&
        config.routes.some((route) => this.router.isActive(route, false)),
    );
    if (!activePanelFound) {
      const firstAvailablePanel = panelConfig.find((p) => p.isAvailable);
      if (firstAvailablePanel) {
        this[`${firstAvailablePanel.name}PanelExpanded`] = true;
      }
    }
  }

  get digitalCardEnabled(): boolean {
    return !!this.auth.appUser?.optionalInterfaces?.digitalCard;
  }

  get contextualQrEnabled(): boolean {
    return !!this.auth.appUser?.optionalInterfaces?.contextualQr;
  }

  get virtualBackgroundsEnabled(): boolean {
    return !!this.auth.appUser?.optionalInterfaces?.virtualBackgrounds;
  }

  get browserExtensionEnabled(): boolean {
    return !!this.auth.appUser?.optionalInterfaces?.browserExtension;
  }

  get selfPrintCardsEnabled(): boolean {
    return !!this.auth.appUser?.optionalInterfaces?.selfPrintCards;
  }

  get emailSignaturesEnabled(): boolean {
    return !!this.auth.appUser?.optionalInterfaces?.emailSignatures;
  }

  get showDigitalCardsNav(): boolean {
    const hasSectionAccess =
      (this.digitalCardEnabled || this.auth.isSuperAdmin) &&
      (this.auth.hasEmployee ||
        this.auth.hasCompanyManagerAccess ||
        this.auth.isSuperAdmin);
    if (!hasSectionAccess) {
      return false;
    }
    return (
      this.auth.isSuperAdmin ||
      this.auth.hasCompanyManagerAccess ||
      this.showContextualQrNav ||
      this.showSelfPrintCardsNav ||
      this.showCardOrdersNav
    );
  }

  get showSignaturesNav(): boolean {
    return (
      this.auth.hasCompanyManagerAccess &&
      (this.emailSignaturesEnabled || this.auth.isSuperAdmin)
    );
  }

  get showBackgroundsNav(): boolean {
    return (
      this.auth.hasCompanyManagerAccess &&
      (this.virtualBackgroundsEnabled || this.auth.isSuperAdmin)
    );
  }

  get showContextualQrNav(): boolean {
    return (
      (this.auth.isSuperAdmin || this.auth.hasCompanyManagerAccess) &&
      (this.contextualQrEnabled || this.auth.isSuperAdmin)
    );
  }

  get showSelfPrintCardsNav(): boolean {
    return (
      (this.auth.isSuperAdmin || this.auth.hasCompanyManagerAccess) &&
      (this.selfPrintCardsEnabled || this.auth.isSuperAdmin)
    );
  }

  get showCardOrdersNav(): boolean {
    return (
      (this.auth.hasEmployee ||
        this.auth.hasCompanyManagerAccess ||
        this.auth.isSuperAdmin) &&
      (this.selfPrintCardsEnabled || this.auth.isSuperAdmin)
    );
  }

  get showBrowserExtensionNav(): boolean {
    return this.browserExtensionEnabled || this.auth.isSuperAdmin;
  }

  get showMyDigitalCardsNav(): boolean {
    return (
      this.auth.hasEmployee &&
      (this.digitalCardEnabled || this.auth.isSuperAdmin)
    );
  }

  get showMyBackgroundsNav(): boolean {
    return (
      this.auth.hasEmployee &&
      (this.virtualBackgroundsEnabled || this.auth.isSuperAdmin)
    );
  }

  isOptionalNavInactive(enabled: boolean): boolean {
    return this.auth.isSuperAdmin && !enabled;
  }

  async loadStartProgress(): Promise<void> {
    if (!this.auth.hasCompanyManagerAccess) {
      this.startProgress = 0;
      return;
    }
    try {
      this.isLoadingProgress = true;
      const response = await this.http
        .get<Record<string, string>>("/start/progress")
        .toPromise();
      this.calculateProgress(response || {});
    } catch {
      this.startProgress = 0;
    } finally {
      this.isLoadingProgress = false;
    }
  }

  private calculateProgress(progress: Record<string, string>): void {
    const steps = ["onboarding", "sync", "installation"];
    this.startProgress = Math.round(
      (steps.filter((step) => progress[step] === "COMPLETED").length /
        steps.length) *
        100,
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
